import React from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import {
  InstitutionProvider,
  useInstitution,
} from "../contexts/InstitutionContext";
import SessionView from "./SessionView";
import TrendlineView from "./TrendlineView";

const PasswordPrompt = ({ onSubmit, error }) => {
  const [password, setPassword] = React.useState("");
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    await onSubmit(password);
    setIsSubmitting(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center p-6">
      <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full">
        <h2 className="text-2xl font-bold text-gray-800 mb-2">
          Institution Access
        </h2>
        <p className="text-gray-600 mb-6">
          Enter password to access this institution's dashboard
        </p>

        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label
              htmlFor="password"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              Password
            </label>
            <input
              type="password"
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2f80ed] focus:border-transparent"
              placeholder="Enter password"
              required
              disabled={isSubmitting}
            />
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full bg-[#2f80ed] text-white py-2 px-4 rounded-lg hover:bg-[#1e5bb8] disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
          >
            {isSubmitting ? "Authenticating..." : "Access Dashboard"}
          </button>
        </form>
      </div>
    </div>
  );
};

const LoadingProgressNotification = ({ progress }) => {
  const active =
    progress && (progress.hasNextPage || progress.isBackgroundSync);
  if (!active) return null;

  const total = Math.max(progress.totalDocuments || 0, 1);
  const loaded = progress.loadedDocuments ?? 0;
  const percentage =
    loaded >= total
      ? 100
      : Math.min(99, Math.floor((loaded / total) * 100));

  const title = progress.isBackgroundSync
    ? "Checking for more sessions"
    : "Loading Sessions";

  return (
    <div className="fixed top-4 right-4 bg-white rounded-lg shadow-lg p-4 max-w-sm z-50 border border-blue-200">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-semibold text-gray-800">{title}</h3>
        <span className="text-xs text-gray-600">{percentage}%</span>
      </div>
      <div className="w-full bg-gray-200 rounded-full h-2.5 mb-2">
        <div
          className="bg-[#2f80ed] h-2.5 rounded-full transition-all duration-300"
          style={{ width: `${percentage}%` }}
        ></div>
      </div>
      <p className="text-xs text-gray-600">
        {Math.min(loaded, total)} / {total} sessions loaded
      </p>
    </div>
  );
};

const InstitutionRoutes = () => {
  const {
    sessions,
    isLoading,
    error,
    isAuthenticated,
    showPasswordPrompt,
    handlePasswordSubmit,
    loadingProgress,
  } = useInstitution();

  if (showPasswordPrompt) {
    return <PasswordPrompt onSubmit={handlePasswordSubmit} error={error} />;
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 p-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#2f80ed] mx-auto mb-4"></div>
            <p className="text-gray-600 mb-2">Loading institution data...</p>
            <p className="text-gray-500 text-sm">
              This is a prototype, it may take up to 1 min to load data
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <LoadingProgressNotification progress={loadingProgress} />
      <Routes>
        <Route path="trendline" element={<TrendlineView />} />
        <Route
          path="session/:sessionId"
          element={<SessionView sessions={sessions} />}
        />
        <Route
          path="/"
          element={
            sessions.length > 0 ? (
              <Navigate to={`session/${sessions[0].recording_id}`} replace />
            ) : (
              <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center">
                <p className="text-gray-600">No sessions available</p>
              </div>
            )
          }
        />
      </Routes>
    </>
  );
};

const InstitutionDashboard = () => {
  return (
    <InstitutionProvider>
      <InstitutionRoutes />
    </InstitutionProvider>
  );
};

export default InstitutionDashboard;
